import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';
import { PackageDropdownContainer } from '../../../../../components/package-dropdown/container';
import Global from '../../../../../store/global-selectors';
import { ShippingServiceSelector } from '../../../../../components/shipping-service-selector';
import { useSelectedItemName } from '../../../../../components/shipping-service-selector/use-selected-item-name';
import { useCarrierGroups } from '../../../../../components/shipping-service-selector/group-hooks/use-carrier-groups';

import style from '../inputs.module.css';
import { OptionDataPropType, DefaultOptionData } from './prop-types-and-default-props';
import { messages } from './messages';

// TODO: includeReturnLabel backend written in V2 and only accepts LabelProviderId and operates on the Primary account
// Underlying ShippingServiceDropdown always returns sellerLabelProviderId so we convert here
const useGetUpdateService = ({ optionData, updateOption, sellerLabelProviders }) => {
  const updateService = useCallback(
    (serviceId, sellerLabelProviderId) => {
      const matchingProvider = sellerLabelProviders.find((lp) => lp.sellerLabelProviderId === sellerLabelProviderId);
      const newData = {
        ...optionData,
        serviceId,
        labelProviderId: get(matchingProvider, 'labelProviderId'),
      };
      updateOption({ id: 'includeReturnLabel', value: newData });
    },
    [optionData, updateOption, sellerLabelProviders],
  );

  return updateService;
};

const useGetUpdatePackage = ({ optionData, updateOption }) => {
  const updatePackage = useCallback(
    (selectedPackage) => {
      const { packageTypeId } = selectedPackage;
      const newData = {
        ...optionData,
        packageTypeId,
      };
      updateOption({ id: 'includeReturnLabel', value: newData });
    },
    [optionData, updateOption],
  );
  return updatePackage;
};

export const ReturnLabelInputs = ({ optionData = DefaultOptionData, selectedPackage, updateOption = noop }) => {
  const serviceSupportsReturnFilter = (service) => get(service, 'features.supportsReturnLabels');
  const serviceIsNotInternationalFilter = (service) => !get(service, 'features.supportsInternationalShipping');
  const sellerLabelProviders = useSelector(Global.selectSellerLabelProviders);
  const allServices = useSelector(Global.selectSellerServices);
  const filteredServices = useMemo(
    () => allServices.filter(serviceSupportsReturnFilter).filter(serviceIsNotInternationalFilter),
    [allServices],
  );

  const { serviceId } = optionData;

  const selectedPackageKey = get(selectedPackage, 'packageId');
  const updateService = useGetUpdateService({
    optionData,
    updateOption,
    sellerLabelProviders,
  });
  const updatePackage = useGetUpdatePackage({ optionData, updateOption });

  const onNewSelectorSelected = useCallback(
    (newService) => {
      updateService(newService.serviceId, newService.sellerLabelProviderId);
    },
    [updateService],
  );

  const selectedServiceItem = useMemo(
    () => ({
      serviceId,
    }),
    [serviceId],
  );
  const selectedItemName = useSelectedItemName(selectedServiceItem);
  const carrierGroups = useCarrierGroups(filteredServices, selectedServiceItem);

  return (
    <div className={style['multiple-rows']}>
      <div className={style['flex-row']}>
        <label>
          <FormattedMessage {...messages.serviceLabel} />
        </label>
        <ShippingServiceSelector
          className={style['package-container']}
          dropdownTogglerClassName={style['bigly-dropdown']}
          onSelected={onNewSelectorSelected}
          selectedItemName={selectedItemName}
          serviceGroups={carrierGroups}
        />
      </div>
      <div className={style['flex-row']}>
        <label>
          <FormattedMessage {...messages.packageLabel} />
        </label>
        <PackageDropdownContainer
          className={style['package-container']}
          dropdownMenuShouldHaveWidthOfButton
          onPackageSelected={updatePackage}
          selectedPackageKey={selectedPackageKey}
          shouldShowDimensions={false}
          simpleToggler
          togglerClassName={style['bigly-dropdown']}
        />
      </div>
    </div>
  );
};

ReturnLabelInputs.propTypes = {
  optionData: OptionDataPropType,
  selectedPackage: PropTypes.object,
  updateOption: PropTypes.func,
};
